import { apiSlice } from './api-slice';
import {
  Response,
  Sortable,
  TeamMember,
  PaginatedTeamMember,
  Partner,
} from '../models';
import { PAGINATION_SIZE } from '../constants';

export const usersApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    getTeamMembers: builder.query<
      Response<Sortable<TeamMember>>,
      PaginatedTeamMember
    >({
      query: ({ page, name, status, companyId, size }) => ({
        url: `fetchCompanyTeamMember/${companyId}`,
        method: 'GET',
        params: {
          status,
          size: size || PAGINATION_SIZE,
          page,
          name,
        },
      }),
      providesTags: result =>
        result?.data.content.length
          ? [
              ...result.data.content.map(({ id }) => ({
                type: 'User' as const,
                id,
              })),
              ...result.data.content.map(({ role: { id } }) => ({
                type: 'Role' as const,
                id,
              })),
            ]
          : ['User'],
    }),
    getTeamMemberById: builder.query<Response<TeamMember>, number>({
      query: MemberId => ({
        url: `teamMember/${MemberId}`,
        method: 'GET',
      }),
      providesTags: (_, __, id) => [{ type: 'User', id }],
    }),
    getPartners: builder.query<Response<Partner>, any>({
      query: () => ({
        url: `/company/partnerBuilding/partners`,
        method: 'GET',
      }),
    }),
    getTeamMembersByRoleId: builder.query<Response<TeamMember[]>, number>({
      query: roleId => ({
        url: `/company/teamMember/fetchByRole/?roleId=${roleId}`,
        method: 'GET',
      }),
      providesTags: (_, __, id) => [{ type: 'User', id }],
    }),
    createTeamMember: builder.mutation<Response<TeamMember>, FormData>({
      query: data => ({
        url: 'company/teamMember',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'User' }],
    }),
    createNewPartner: builder.mutation<Response<Partner>, { name: string }>({
      query: data => ({
        url: 'company/partnerBuilding/partner',
        method: 'POST',
        body: data,
      }),
    }),
    updateTeamMember: builder.mutation<Response<TeamMember>, FormData>({
      query: data => ({
        url: 'company/teamMember',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: result => {
        // TODO: add invalidatesTags coming from data from backend
        return [{ type: 'User', id: result?.data.id }];
      },
    }),
    deleteTeamMembers: builder.mutation<Response<null>, { id: number }[]>({
      query: data => ({
        url: 'company/teamMember',
        method: 'DELETE',
        body: data,
      }),
      invalidatesTags: () => {
        // TODO: add invalidatesTags coming from data from backend
        return [{ type: 'User' }];
      },
    }),
    reassignTeamMemberRole: builder.mutation<
      Response<null>,
      { teamMemberId: number; roleId: number }[]
    >({
      query: data => ({
        url: 'company/teamMember/reassignRole',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_, __, data) => {
        return data.map(({ teamMemberId }) => ({
          type: 'User',
          id: teamMemberId,
        }));
      },
    }),
    resendVerificationEmail: builder.mutation<Response<null>, { id: number }>({
      query: data => ({
        url: 'company/teamMember/resendVerificationEmail',
        method: 'POST',
        body: data,
      }),
    }),
    getTeamMembersByProjectId: builder.query<
      Response<TeamMember[]>,
      {
        projectId: string;
        page: number;
        size?: number;
        name?: string;
      }
    >({
      query: ({ projectId, page, size, name }) => ({
        url: `company/project/${projectId}/teamMember`,
        params: {
          size: size || PAGINATION_SIZE,
          page,
          name,
        },
        method: 'GET',
      }),
      providesTags: result =>
        result?.data.length
          ? [
              ...result.data.map(({ id }) => ({
                type: 'TeamMember' as const,
                id,
              })),
              ...result.data.map(({ id }) => ({
                type: 'User' as const,
                id,
              })),
              ...result.data.map(({ role: { id } }) => ({
                type: 'Role' as const,
                id,
              })),
            ]
          : ['TeamMember'],
    }),
    getTeamMembersByPhaseId: builder.query<
      Response<Sortable<TeamMember>>,
      {
        projectId: string;
        phaseId: string;
        page: number;
        size?: number;
        name: string;
      }
    >({
      query: ({ phaseId, projectId, page, size, name }) => ({
        url: `company/project/${projectId}/phase/${phaseId}/teamMember`,
        params: {
          size: size || PAGINATION_SIZE,
          page,
          name,
        },
        method: 'GET',
      }),
      providesTags: result =>
        result?.data.content.length
          ? [
              ...result.data.content.map(({ id }) => ({
                type: 'TeamMember' as const,
                id,
              })),
              ...result.data.content.map(({ id }) => ({
                type: 'User' as const,
                id,
              })),
              ...result.data.content.map(({ role: { id } }) => ({
                type: 'Role' as const,
                id,
              })),
            ]
          : ['TeamMember'],
    }),
    assignTeamMemberToPhase: builder.mutation<
      Response<TeamMember>,
      {
        teamMemberId: string;
        projectId: string;
        phaseId?: string;
      }
    >({
      query: ({ phaseId, projectId, teamMemberId }) => ({
        url: `company/teamMember/assignOnProject/${projectId}`,
        method: 'POST',
        body: { phaseId, teamMemberId },
      }),
      invalidatesTags: () => {
        return [{ type: 'TeamMember' }];
      },
    }),
    unAssignTeamMemberToPhase: builder.mutation<
      Response<TeamMember>,
      {
        teamMemberId: string;
        projectId: string;
        phaseId?: string;
      }
    >({
      query: ({ phaseId, projectId, teamMemberId }) => ({
        url: `company/teamMember/unAssignFromProject/${projectId}`,
        method: 'POST',
        body: { phaseId, teamMemberId },
      }),
      invalidatesTags: res => {
        return [{ type: 'TeamMember', id: res?.data.id }];
      },
    }),
  }),
});

export const {
  useGetPartnersQuery,
  useGetTeamMembersQuery,
  useGetTeamMemberByIdQuery,
  useGetTeamMembersByRoleIdQuery,
  useUpdateTeamMemberMutation,
  useCreateTeamMemberMutation,
  useCreateNewPartnerMutation,
  useDeleteTeamMembersMutation,
  useReassignTeamMemberRoleMutation,
  useResendVerificationEmailMutation,
  useGetTeamMembersByProjectIdQuery,
  useGetTeamMembersByPhaseIdQuery,
  useAssignTeamMemberToPhaseMutation,
  useUnAssignTeamMemberToPhaseMutation,
} = usersApiSlice;
